import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { getSourceBrandLocationAttributesEndpointIdentifier } from './use-get-source-brand-location-attributes-api';

const endpoint = 'ums/v1/crm/management/source-brand-location-attributes';
export const updateSourceBrandLocationAttributeEndpointIdentifier = endpoint;

interface BodyType {
  key?: string;
  value?: string;
  valueCast?: 'string' | 'number' | 'boolean' | 'json';
}

interface MutationPayload {
  id: number;
  data: BodyType;
}

type TData = {
  message: string;
};
type TError = AxiosError<{ message: string }>;
type Options = UseMutationOptions<TData, TError, MutationPayload>;
type ReturnType = UseMutationResult<TData, TError, MutationPayload>;

export function useUpdateSourceBrandLocationAttributeApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async ({ id, data }: MutationPayload) => {
    try {
      const response = await http.put(`${endpoint}/${id}`, data);

      toast({
        description: 'Location attribute updated successfully',
        status: 'success',
      });

      return response.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  const { ...otherOptions } = options || {};

  return useMutationWrapper(mutationFn, {
    onSuccess: () => {
      // Only invalidate attribute queries for the main list
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [queryEndpoint, params] = query.queryKey;
          return (
            queryEndpoint === getSourceBrandLocationAttributesEndpointIdentifier &&
            typeof params === 'object' &&
            params !== null &&
            !Array.isArray(params)
          );
        },
      });

      // Get the location ID from the updated attribute to invalidate specific location's attributes
      // Note: We need to determine which location this attribute belongs to
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [queryEndpoint, type] = query.queryKey;
          return queryEndpoint === endpoint && type === 'location';
        },
      });
    },
    ...otherOptions,
  });
}
